import { useMutation } from "@tanstack/react-query";
import { getApiConfig } from "@/settings/api";
import environment from "@/settings/environment";
import {
  getFormattedDeviceFingerPrintID,
  clearString,
} from "@/utils/functions";
import { getCustomerData, PAYMENT_TYPE } from "@/redux/ducks/customer";
import apiPCI from "@/settings/apiPCI";
import { useDispatch } from "react-redux";

interface PaymentMethod {
  paymentModality?: string;
  paymentForm?: string;
  accountDueDay?: string;
  invoiceDeliveryMethod?: string;
  flagAuthorizesNFE?: boolean;
  flagChangesModality?: boolean;
  deviceFingerprintID?: string;
  creditCard?: CreditCard;
  journey?: string;
}

export enum PAYMENT_MODE {
  PRE_PAGO_FULL = "PRE PAGO FULL",
  BOLETO_PRE = "BOLETO PRÉ-PAGO",
  DEBITO_POSTECIPADO = "DEBITO POSTECIPADO NO CARTÃO DE CRÉDITO",
}

export interface CreditCard {
  flagCode: string;
  expirationMonth: string;
  expirationYear: string;
  cardNumber: string;
}

export interface PaymentMethodResponse {
  data?: ResponseData;
}

interface ResponseData {
  data?: Protocol;
  status?: Status;
}

interface Protocol {
  protocol: string;
}

interface Status {
  codigoRetorno?: number | string;
  mensagem?: string;
}

const updatePaymentMethod = async (
  paymentData: PaymentMethod,
): Promise<PaymentMethodResponse> => {
  try {
    const { apiInstance } = getApiConfig(true);
    const response: PaymentMethodResponse = await apiInstance.put(
      "/ff-account/v1/paymentMethod",
      paymentData,
    );

    if (!response.data?.data?.protocol) {
      throw new Error(
        response.data?.status?.mensagem ||
          "Falha ao alterar o método de pagamento.",
      );
    }

    return response;
  } catch (error) {
    console.error("Erro ao alterar o método de pagamento:", error);
    throw new Error("Falha ao alterar o método de pagamento");
  }
};

const tokenizeCard = async (cardNumber: string): Promise<string> => {
  try {
    const response = await apiPCI.post("/api/v1/tokenizing", {
      dataToToken: clearString(cardNumber),
    });

    if (!response.data?.token) {
      throw new Error("Falha ao tokenizar o cartão.");
    }

    return response.data.token;
  } catch (error) {
    console.error("Erro ao tokenizar o cartão:", error);
    throw new Error("Falha ao tokenizar o cartão");
  }
};

const getPaymentDetail = (paymentMode: PAYMENT_MODE) => {
  switch (paymentMode) {
    case PAYMENT_MODE.PRE_PAGO_FULL:
      return {
        paymentModality: "4",
        paymentForm: "1",
      };
    case PAYMENT_MODE.BOLETO_PRE:
      return {
        paymentModality: "1",
        paymentForm: "2",
      };
    case PAYMENT_MODE.DEBITO_POSTECIPADO:
      return {
        paymentModality: "2",
        paymentForm: "1",
      };
  }
};

export const useChangePaymentMethod = (
  visitorId: string,
  paymentMode: PAYMENT_MODE,
  account?: any,
) => {
  const journey = `APP-${environment?.api?.prefix?.toUpperCase() ?? ""}${account?.paymentType === PAYMENT_TYPE.IS_CARD ? "AlteracaoDadosCartao" : "AlteracaoFormaPagamento"}`;
  const deviceFingerprintID = getFormattedDeviceFingerPrintID(visitorId ?? "");
  const dispatch = useDispatch();
  const { paymentModality, paymentForm } = getPaymentDetail(paymentMode);

  const mutationAsync = useMutation({
    mutationFn: (paymentData: PaymentMethod) =>
      updatePaymentMethod(paymentData),
  });

  const handleChangePaymentMethodAsync = async (creditCard?: CreditCard) => {
    let tokenizedCardNumber = undefined;

    if (creditCard?.cardNumber) {
      tokenizedCardNumber = await tokenizeCard(creditCard.cardNumber);
    }

    const paymentData: PaymentMethod = {
      paymentModality,
      paymentForm,
      accountDueDay: "1",
      invoiceDeliveryMethod: "0",
      flagAuthorizesNFE: true,
      flagChangesModality: true,
      deviceFingerprintID,
      creditCard: creditCard
        ? {
            ...creditCard,
            cardNumber: tokenizedCardNumber || creditCard.cardNumber,
          }
        : undefined,
      journey,
    };

    await mutationAsync.mutateAsync(paymentData);
    dispatch(getCustomerData());
  };

  return {
    handleChangePaymentMethod: handleChangePaymentMethodAsync,

    paymentData: {
      paymentModality,
      paymentForm,
      accountDueDay: "1",
      invoiceDeliveryMethod: "0",
      flagAuthorizesNFE: true,
      flagChangesModality: true,
      deviceFingerprintID,
      journey,
    },

    isLoading: mutationAsync.isPending,
    isSuccess: mutationAsync.isSuccess,
    error: mutationAsync.error,
    data: mutationAsync.data,
  };
};
